import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, api } from '../api/client';
import { AuthBrand } from '../components/AuthBrand';
import { AuthPasswordField } from '../components/AuthField';
import { PasswordValidationRules } from '../components/PasswordValidationRules';
import { AuthSplitCopy } from '../components/AuthSplitCopy';
import { AuthSplitDecor } from '../components/AuthSplitDecor';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ThemeToggle } from '../components/ThemeToggle';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { isStrongPassword, normalizePasswordInput } from '../lib/password';

/**
 * Lands from the link emailed by POST /auth/password-reset/request.
 * Sets a new password and sends the customer to /login — never issues a session.
 */
export function ResetPassword() {
  const { dict } = useLanguage();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<'checking' | 'valid' | 'invalid'>(
    token ? 'checking' : 'invalid',
  );

  const missingToken = !token;
  const passwordsMatch = confirmPassword === '' || confirmPassword === password;
  const passwordStrong = isStrongPassword(password);
  const passwordInvalid = password !== '' && !passwordStrong;
  const canSubmit = !busy && passwordStrong && password === confirmPassword && confirmPassword !== '';

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .validatePasswordReset(token)
      .then(() => {
        if (!cancelled) setTokenStatus('valid');
      })
      .catch(() => {
        if (!cancelled) setTokenStatus('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const normalizedPassword = normalizePasswordInput(password);
    const normalizedConfirm = normalizePasswordInput(confirmPassword);
    if (normalizedPassword !== password) setPassword(normalizedPassword);
    if (normalizedConfirm !== confirmPassword) setConfirmPassword(normalizedConfirm);
    if (normalizedPassword !== normalizedConfirm) {
      setError(dict.resetPassword.errPasswordMismatch);
      return;
    }
    if (!isStrongPassword(normalizedPassword)) {
      setError(dict.resetPassword.errWeakPassword);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.confirmPasswordReset(token, normalizedPassword);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(dict.resetPassword.errTooMany);
      } else if (err instanceof ApiError && err.is('/problems/weak-password')) {
        setError(dict.resetPassword.errWeakPassword);
      } else if (err instanceof ApiError && err.is('/problems/invalid-or-expired-token')) {
        setError(dict.resetPassword.errInvalidToken);
      } else {
        setError(dict.resetPassword.errGeneric);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen auth-split">
      <div className="auth-theme-toggle">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>

      <AuthBrand />

      <div className="auth-split-panel auth-split-panel--signup">
        <AuthSplitDecor />
        <AuthSplitCopy
          eyebrow={dict.resetPassword.welcomeEyebrow}
          headline={dict.resetPassword.welcomeHeadline}
          body={dict.resetPassword.welcomeBody}
        />

        <div className="auth-split-form">
          <div className="auth-split-form-inner">
            {done ? (
              <>
                <h1>{dict.resetPassword.successTitle}</h1>
                <p className="auth-lede">{dict.resetPassword.successBody}</p>
                <Link to="/login" className="btn-block">
                  {dict.resetPassword.goToSignIn}
                </Link>
              </>
            ) : missingToken ? (
              <>
                <h1>{dict.resetPassword.failedTitle}</h1>
                <p className="auth-lede">{dict.resetPassword.missingTokenBody}</p>
                <Link to="/forgot-password" className="btn-block">
                  {dict.resetPassword.requestNewLink}
                </Link>
              </>
            ) : tokenStatus === 'checking' ? (
              <p className="auth-lede">{dict.resetPassword.checkingToken}</p>
            ) : tokenStatus === 'invalid' ? (
              <>
                <h1>{dict.resetPassword.failedTitle}</h1>
                <p className="auth-lede">{dict.resetPassword.invalidTokenBody}</p>
                <Link to="/forgot-password" className="btn-block">
                  {dict.resetPassword.requestNewLink}
                </Link>
              </>
            ) : (
              <form onSubmit={(e) => void submit(e)}>
                <h1>{dict.resetPassword.title}</h1>
                <p className="auth-lede">{dict.resetPassword.lede}</p>

                {error && <div className="err">{error}</div>}

                <AuthPasswordField
                  id="newPassword"
                  label={dict.resetPassword.passwordLabel}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  invalid={passwordInvalid}
                  aria-describedby={password !== '' ? 'reset-password-rules' : undefined}
                  showPassword={showPassword}
                  onToggleShow={() => setShowPassword(!showPassword)}
                  showLabel={dict.resetPassword.showPassword}
                  hideLabel={dict.resetPassword.hidePassword}
                  message={<PasswordValidationRules password={password} id="reset-password-rules" />}
                />

                <AuthPasswordField
                  id="confirmPassword"
                  label={dict.resetPassword.confirmPasswordLabel}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  invalid={!passwordsMatch}
                  showPassword={showConfirm}
                  onToggleShow={() => setShowConfirm(!showConfirm)}
                  showLabel={dict.resetPassword.showPassword}
                  hideLabel={dict.resetPassword.hidePassword}
                  message={
                    !passwordsMatch ? <div className="err">{dict.resetPassword.errPasswordMismatch}</div> : null
                  }
                />

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="btn-block"
                >
                  {busy ? dict.resetPassword.saving : dict.resetPassword.savePassword}
                </button>

                <p className="note">
                  <Link to="/forgot-password">{dict.resetPassword.requestNewLink}</Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
